import { Principal } from "@dfinity/principal";
import * as chrono from "chrono-node";
import { ROLE_MEMBER } from "../constants";
import { parseBigInt, random64 } from "../utils";
import { ValidationErrors } from "../utils/validation";
import { chatIdentifiersEqual, } from "./chat";
import { communityIdentifiersEqual, } from "./community";
export const MIN_BOT_NAME_LENGTH = 5;
export const MIN_COMMAND_NAME_LENGTH = 3;
export const MIN_PARAM_NAME_LENGTH = 1;
function defaultCommonParam(param) {
    return {
        name: param?.name ?? "",
        description: param?.description ?? "",
        placeholder: param?.placeholder ?? "",
        required: param?.required ?? true,
    };
}
export function defaultBooleanParam(param) {
    return {
        kind: "boolean",
        ...defaultCommonParam(param),
    };
}
export function defaultStringParam(param) {
    return {
        kind: "string",
        ...defaultCommonParam(param),
        minLength: 0,
        maxLength: 1000,
        choices: [],
        multi_line: false,
    };
}
export function defaultIntegerParam(param) {
    return {
        kind: "integer",
        ...defaultCommonParam(param),
        minValue: BigInt(0),
        maxValue: BigInt(1000),
        choices: [],
    };
}
export function defaultDecimalParam(param) {
    return {
        kind: "decimal",
        ...defaultCommonParam(param),
        minValue: 0,
        maxValue: 1000,
        choices: [],
    };
}
export function defaultUserParam(param) {
    return {
        kind: "user",
        ...defaultCommonParam(param),
    };
}
export function defaultDateTimeParam(param) {
    return {
        kind: "dateTime",
        ...defaultCommonParam(param),
        future_only: false,
    };
}
export function emptyBotCommand() {
    return {
        name: "",
        description: "",
        params: [],
        defaultRole: ROLE_MEMBER,
        permissions: emptyExternalBotPermissions(),
        directMessages: false,
    };
}
export function emptyExternalBotPermissions() {
    return {
        chatPermissions: [],
        communityPermissions: [],
        messagePermissions: [],
    };
}
export function emptyGrantedBotPermissions() {
    return {
        command: {
            chatPermissions: [],
            communityPermissions: [],
            messagePermissions: [],
        },
        autonomous: undefined,
    };
}
export function flattenCommandPermissions(definition) {
    return definition.commands.reduce((p, c) => {
        return mergePermissions(p, c.permissions);
    }, emptyExternalBotPermissions());
}
export function definitionToPermissions(definition) {
    return {
        command: flattenCommandPermissions(definition),
        autonomous: definition.autonomousConfig?.permissions,
    };
}
function mergeLists(l1, l2) {
    return [...new Set([...l1, ...l2])];
}
function mergePermissions(p1, p2) {
    return {
        chatPermissions: mergeLists(p1.chatPermissions, p2.chatPermissions),
        communityPermissions: mergeLists(p1.communityPermissions, p2.communityPermissions),
        messagePermissions: mergeLists(p1.messagePermissions, p2.messagePermissions),
    };
}
export function setifyCommandPermissions(perm) {
    return {
        chatPermissions: new Set(perm.chatPermissions),
        communityPermissions: new Set(perm.communityPermissions),
        messagePermissions: new Set(perm.messagePermissions),
    };
}
function hasEveryPermissionOfType(required, granted, type) {
    const r = required[type];
    const g = granted[type];
    return r.every((p) => g.has(p));
}
export function hasEveryRequiredPermission(required, granted) {
    const grantedSet = setifyCommandPermissions(granted);
    return (hasEveryPermissionOfType(required, grantedSet, "chatPermissions") &&
        hasEveryPermissionOfType(required, grantedSet, "communityPermissions") &&
        hasEveryPermissionOfType(required, grantedSet, "messagePermissions"));
}
export function emptyBotInstance(ownerId) {
    return {
        kind: "external_bot",
        id: "",
        ownerId,
        name: "",
        endpoint: "",
        definition: {
            kind: "bot_definition",
            description: "",
            commands: [],
            autonomousConfig: undefined,
            defaultSubscriptions: undefined,
            dataEncoding: undefined,
        },
        registrationStatus: { kind: "private" },
    };
}
function singleStringParam(params) {
    return params.length === 1 && params[0].kind === "string";
}
export function createArgsFromSchema(params, maybeArgs) {
    if (singleStringParam(params)) {
        return [{ kind: "string", name: params[0].name, value: maybeArgs.join(" ") }];
    }
    return params.map((p, i) => {
        switch (p.kind) {
            case "user":
                return { kind: "user", name: p.name };
            case "boolean": {
                const boolVal = (maybeArgs[i] ?? "false").toLocaleLowerCase() === "true";
                return { kind: "boolean", name: p.name, value: boolVal };
            }
            case "integer": {
                let value = parseBigInt(maybeArgs[i]) ?? null;
                if (p.choices.length > 0) {
                    value = p.choices.find((c) => c.value === value)?.value ?? null;
                }
                return { kind: p.kind, name: p.name, value };
            }
            case "decimal": {
                const numParam = Number(maybeArgs[i]);
                let value = isNaN(numParam) ? null : numParam;
                if (p.choices.length > 0) {
                    value = p.choices.find((c) => c.value === value)?.value ?? null;
                }
                return { kind: p.kind, name: p.name, value };
            }
            case "string": {
                let strParam = maybeArgs[i] ?? "";
                if (p.choices.length > 0) {
                    strParam =
                        p.choices.find((c) => c.name.toLocaleLowerCase() === strParam.toLocaleLowerCase() ||
                            c.value.toLocaleLowerCase() === strParam.toLocaleLowerCase())?.value ?? "";
                }
                return { kind: "string", name: p.name, value: strParam };
            }
            case "dateTime": {
                const arg = maybeArgs[i];
                return {
                    name: p.name,
                    kind: "dateTime",
                    value: parseBigInt(arg) ?? parseDateTime(arg),
                };
            }
        }
    });
}
function parseDateTime(value) {
    const now = new Date();
    const date = chrono.parseDate(value, { instant: now, timezone: -now.getTimezoneOffset() }, { forwardDate: true });
    if (date == null) {
        return null;
    }
    return BigInt(date.getTime());
}
export function argIsValid(schema, arg) {
    if (schema.kind === "user" && arg.kind === "user") {
        return !schema.required || arg.userId !== undefined;
    }
    else if (schema.kind === "boolean" && arg.kind === "boolean") {
        return !schema.required || arg.value !== undefined;
    }
    else if (schema.kind === "string" && arg.kind === "string") {
        // TODO - this is not quite right because it doesn't account for choices.
        // if we specify choices then the value of the arg must match one of the choices.
        // this means that we can currently do things like /faq some_old_nonsense
        return (!schema.required ||
            (arg.value !== undefined &&
                arg.value.length > schema.minLength &&
                arg.value.length < schema.maxLength));
    }
    else if (schema.kind === "integer" && arg.kind === "integer") {
        return ((!schema.required && arg.value === null) ||
            (arg.value !== null && arg.value >= schema.minValue && arg.value <= schema.maxValue));
    }
    else if (schema.kind === "decimal" && arg.kind === "decimal") {
        return ((!schema.required && arg.value === null) ||
            (arg.value !== null && arg.value >= schema.minValue && arg.value <= schema.maxValue));
    }
    else if (schema.kind === "dateTime" && arg.kind === "dateTime") {
        return !schema.required || (arg.value !== undefined && arg.value !== null);
    }
    return false;
}
export function i18nKey(key, params) {
    return {
        kind: "resource_key",
        key,
        params,
        lowercase: false,
    };
}
// This is used for all names: bot, command, param
export function validBotComponentName(name, min) {
    const errors = [];
    if (name.length < min) {
        errors.push(i18nKey("bots.builder.errors.minLength", { n: min }));
    }
    const regex = /^[a-zA-Z0-9_]+$/;
    if (!regex.test(name)) {
        errors.push(i18nKey("bots.builder.errors.alphaOnly"));
    }
    return errors;
}
function validatePrincipal(p, mode) {
    if (mode === "update" && p === "")
        return true;
    try {
        Principal.fromText(p);
        return true;
    }
    catch (_) {
        return false;
    }
}
export function validEndpoint(endpoint) {
    return validOrigin(endpoint);
}
export function validateBot(principal, bot, mode) {
    const errors = new ValidationErrors();
    errors.addErrors(`bot_name`, validBotComponentName(bot.name, MIN_BOT_NAME_LENGTH));
    if (bot.ownerId === "") {
        errors.addErrors("bot_owner", i18nKey("bots.builder.errors.owner"));
    }
    if (!validEndpoint(bot.endpoint)) {
        errors.addErrors("bot_endpoint", i18nKey("bots.builder.errors.endpoint"));
    }
    if (!validatePrincipal(principal, mode)) {
        errors.addErrors("bot_principal", i18nKey("bots.builder.errors.principal"));
    }
    if (bot.definition.commands.length === 0 && bot.definition.autonomousConfig === undefined) {
        errors.addErrors("no_commands", i18nKey("bots.builder.errors.noCommands"));
    }
    if (containsDuplicateCommands(bot.definition.commands)) {
        errors.addErrors("duplicate_commands", i18nKey("bots.builder.errors.duplicateCommands"));
    }
    bot.definition.commands.forEach((command, i) => {
        if (!validateCommand(command, `command_${i}`, errors)) {
            errors.addErrors(`command_${i}`, i18nKey("Command has errors"));
        }
    });
    return errors;
}
function validateCommand(command, errorPath, errors) {
    let valid = true;
    const nameErrors = validBotComponentName(command.name, MIN_COMMAND_NAME_LENGTH);
    if (nameErrors.length > 0) {
        errors.addErrors(`${errorPath}_name`, nameErrors);
        valid = false;
    }
    if (containsDuplicateParams(command.params)) {
        errors.addErrors(`${errorPath}_duplicate_params`, i18nKey("bots.builder.errors.duplicateParams"));
    }
    command.params.forEach((p, i) => {
        const paramPath = `${errorPath}_param_${i}`;
        if (!validateParameter(p, paramPath, errors)) {
            errors.addErrors(paramPath, i18nKey("Parameter has errors"));
            valid = false;
        }
    });
    return valid;
}
function containsDuplicateCommands(commands) {
    const set = new Set(commands.map((c) => c.name));
    return set.size < commands.length;
}
function containsDuplicateParams(params) {
    const set = new Set(params.map((p) => p.name));
    return set.size < params.length;
}
function validateParameter(param, errorPath, errors) {
    let valid = true;
    const nameErrors = validBotComponentName(param.name, MIN_PARAM_NAME_LENGTH);
    if (nameErrors.length > 0) {
        errors.addErrors(`${errorPath}_name`, nameErrors);
        valid = false;
    }
    if (param.kind === "string") {
        param.choices.forEach((c, i) => {
            if (c.name.length < MIN_PARAM_NAME_LENGTH) {
                errors.addErrors(`${errorPath}_choices_${i}_name`, i18nKey("bots.builder.errors.minLength", { n: MIN_PARAM_NAME_LENGTH }));
                valid = false;
            }
            if (c.value.length < MIN_PARAM_NAME_LENGTH) {
                errors.addErrors(`${errorPath}_choices_${i}_value`, i18nKey("bots.builder.errors.minLength", { n: MIN_PARAM_NAME_LENGTH }));
                valid = false;
            }
        });
    }
    if (param.kind === "integer") {
        param.choices.forEach((c, i) => {
            if (c.name.length < MIN_PARAM_NAME_LENGTH) {
                errors.addErrors(`${errorPath}_choices_${i}_name`, i18nKey("bots.builder.errors.minLength", { n: MIN_PARAM_NAME_LENGTH }));
                valid = false;
            }
        });
    }
    if (param.kind === "decimal") {
        param.choices.forEach((c, i) => {
            if (c.name.length < MIN_PARAM_NAME_LENGTH) {
                errors.addErrors(`${errorPath}_choices_${i}_name`, i18nKey("bots.builder.errors.minLength", { n: MIN_PARAM_NAME_LENGTH }));
                valid = false;
            }
        });
    }
    return valid;
}
function validOrigin(origin) {
    if (!origin)
        return false;
    try {
        const o = new URL(origin);
        return o.origin === origin;
    }
    catch (_) {
        return false;
    }
}
export function botActionScopeFromExecutionContext(ctx) {
    switch (ctx.kind) {
        case "community":
            return {
                kind: "community_scope",
                communityId: ctx,
            };
        default:
            return {
                kind: "chat_scope",
                chatId: ctx,
                messageId: random64(),
                threadRootMessageIndex: undefined,
            };
    }
}
export function installationLocationsEqual(a, b) {
    if (a.kind !== b.kind)
        return false;
    switch (a.kind) {
        case "community":
            return communityIdentifiersEqual(a, b);
        default:
            return chatIdentifiersEqual(a, b);
    }
}
export function botActionScopeFromInstallLocation(location) {
    switch (location.kind) {
        case "community":
            return {
                kind: "community_scope",
                communityId: { kind: "community", communityId: location.communityId },
            };
        default:
            return {
                kind: "chat_scope",
                chatId: location,
                messageId: random64(),
                threadRootMessageIndex: undefined,
            };
    }
}
export function commandSupportsDirectMessages(command) {
    const [first, ...rest] = command.params;
    return command.directMessages && first?.kind === "string" && rest.every((p) => !p.required);
}
export function directMessageCommandInstance(bot, text) {
    const command = bot.definition.commands.find(commandSupportsDirectMessages);
    if (command) {
        return {
            kind: "external_bot",
            id: bot.id,
            endpoint: bot.endpoint,
            command: {
                name: command.name,
                arguments: createArgsFromSchema(command.params, [text]),
                placeholder: command.placeholder,
            },
        };
    }
}
export function emptyWebhookInstance() {
    return {
        id: "",
        name: "",
    };
}
